import {
  formatPhoneNumberUsingFormats,
  removePhoneNumberFormatting,
  validateExtension,
  validatePhoneNumber,
} from "../../utils/utilPhoneNumbers";
import { isOnlyLetters, validateEmailAddress } from "../../validation/input-validations";
import { compareObjects, validateField } from "../base-logic";
import store from "../../redux/store";
import { contactsUiToContacts } from "../../utils/utilContactsParser";
import { isServiceAllowed } from "../../utils/utilServices";
import { updateContactsAccordionError, updateContactsErrors } from "../../redux/actions/CompanyDirectoryActions";

export const COMPANY_DIRECTORY_KEY = "company-directory";

export const invalidCompanyDirectoryKeys = ["no-routing-question", "invalid-contact", "min-contacts"];

export const getContactErrors = (contact) => {
  const config = store.getState().companyDirectory.config;
  const { agentLanguage, voices, updatedVoice } = store.getState().info;
  const selectedVoice = voices?.find((voice) => voice.id === updatedVoice);
  const selectedVoiceLanguage = selectedVoice?.language || agentLanguage || "en-US";
  const errors = {};

  errors.name = validateField(
    true,
    "Name",
    contact.name,
    config.minNameCharacters,
    selectedVoiceLanguage,
    isOnlyLetters
  );
  errors.description = validateField(
    config.minDescriptionCharacters > 0,
    "Description",
    contact.description,
    config.minDescriptionCharacters,
    selectedVoiceLanguage,
    isOnlyLetters
  );

  errors.noDestinations =
    contact.destinations.length === 0 || contact.destinations.every((d) => d.value === "")
      ? "Contact requires at least one destination or email address to be filled in"
      : "";
  errors.destinations = validateDestinations(contact);

  return errors;
};

export const getContactFieldError = (contact, key, value) => {
  const config = store.getState().companyDirectory.config;
  const { updatedVoice } = store.getState().info.setup;
  const { agentLanguage, voices } = store.getState().info;
  const selectedVoice = voices?.find((voice) => voice.id === updatedVoice);
  const selectedVoiceLanguage = selectedVoice?.language || agentLanguage || "en-US";

  switch (key) {
    case "name":
      return validateField(true, "Name", value, config.minNameCharacters, selectedVoiceLanguage, isOnlyLetters);
    case "description":
      return validateField(
        config.minDescriptionCharacters > 0,
        "Description",
        value,
        config.minDescriptionCharacters,
        selectedVoiceLanguage,
        isOnlyLetters
      );
    case "destinations":
      return validateDestinations(contact);
    default:
      return "";
  }
};

export const validateDestinations = (contact) => {
  var errors = [];

  errors = contact.destinations.map((destination) => {
    return validateDestination(contact, destination);
  });

  return errors;
};

export const validateDestination = (contact, destination) => {
  const config = store.getState().companyDirectory.config;

  switch (destination.type) {
    case "email":
      return validateField(
        contact.isDefault,
        destination.type,
        destination.value,
        config.minEmailCharacters,
        "",
        validateEmailAddress,
        "Invalid email address"
      );
    case "phone_number":
      return validateField(
        false,
        destination.type.replace("_", " "),
        formatPhoneNumberUsingFormats(destination.value),
        8,
        "",
        validatePhoneNumber,
        "Invalid phone number"
      );
    case "extension":
      return validateField(true, destination.type.replace("_", " "), destination.value, 3, "", validateExtension, "");
    default:
      return validateField(true, destination.type.replace("_", " "), destination.value, 0);
  }
};

export const hasChangesInCompanyDirectory = () => {
  const companyDirectory = store.getState().companyDirectory;

  if (!companyDirectory) {
    return false;
  }

  if (!compareObjects(companyDirectory.contacts, companyDirectory.updatedContacts)) {
    return true;
  }

  if (companyDirectory.netsapiensSyncDomain !== companyDirectory.updatedNetsapiensSyncDomain) {
    return true;
  }

  return false;
};

export const validateCompanyDirectory = () => {
  const companyDirectory = store.getState().companyDirectory;

  if (!companyDirectory || !isServiceAllowed(COMPANY_DIRECTORY_KEY)) {
    return { valid: true };
  }

  if (companyDirectory.updatedContacts.length === 0 && companyDirectory.config.minContactListSize === 0) {
    return { valid: false, type: "no-contacts", message: `Please add at least one contact` };
  }

  if (
    companyDirectory.config.minContactListSize > 0 &&
    companyDirectory.updatedContacts.length < companyDirectory.config.minContactListSize
  ) {
    const errorMessage =
      "You need to add at least " +
      (companyDirectory.config.minContactListSize === 1
        ? "1 contact"
        : companyDirectory.config.minContactListSize + " contacts");

    store.dispatch(updateContactsAccordionError(errorMessage));
    return { valid: false, type: "min-contacts", elementId: "contacts-accordion" };
  }

  for (var i = 0; i < companyDirectory.updatedContacts.length; i++) {
    const contact = companyDirectory.updatedContacts[i];
    const isValidContact = validateContact(i, contact);
    if (!isValidContact.valid) {
      // update errors item
      const contactErrors = [...companyDirectory.contactErrors];
      contactErrors[i] = getContactErrors(contact);
      store.dispatch(updateContactsErrors(contactErrors));

      return isValidContact;
    }
  }

  return { valid: true };
};

export const validateContact = (index, contact) => {
  const config = store.getState().companyDirectory.config;
  var response = {
    valid: false,
    type: "invalid-contact",
    message: "",
    elementId: `contact-${index}`,
    accordionIndex: index,
  };
  if (contact.name.length < config.minNameCharacters) {
    return { ...response, message: `Contact(${index}) - Invalid name` };
  }

  if (contact.description.length < config.minDescriptionCharacters) {
    return { ...response, message: `Contact(${index}) - Invalid description` };
  }

  if (contact.destinations.length === 0 || contact.destinations.every((d) => d.value === "")) {
    return { ...response, message: `Contact requires at least one destination or email address to be filled in` };
  }

  for (var i = 0; i < contact.destinations.length; i++) {
    const destination = contact.destinations[i];
    if (destination.type === "email") {
      if (
        contact.isDefault &&
        destination.value.length < config.minEmailCharacters &&
        !validateEmailAddress(destination.value)
      ) {
        return { ...response, message: `Contact(${index}) - Invalid Destination email` };
      }
    }
    if (destination.type === "phone_number" && !validatePhoneNumber(formatPhoneNumberUsingFormats(destination.value))) {
      return { ...response, message: `Contact(${index}) - Invalid Destination phone number` };
    }
    if (destination.type === "extension" && validateExtension(destination.value).length > 0) {
      return { ...response, message: `Contact(${index}) - Invalid Destination extension` };
    }
    if (destination.type === "custom" && destination.value.length === 0) {
      return { ...response, message: `Contact(${index}) - Invalid Destination custom` };
    }
  }

  return { valid: true };
};

export const getCompanyDirectorySaveObject = () => {
  const companyDirectory = store.getState().companyDirectory;
  const destinations = store.getState().info.destinations;

  if (!companyDirectory) {
    return null;
  }

  const trimmedContacts = companyDirectory.updatedContacts.map((contact) => {
    return {
      ...contact,
      name: contact.name.trim(),
      description: contact.description.trim(),
      destinations: contact.destinations.map((destination) => {
        return {
          ...destination,
          value: destination.value.trim(),
        };
      }),
    };
  });

  const newDestinations = destinations.filter((destination) => {
    return destination.type === "extension" || destination.type === "voicemail";
  });

  // TODO: Add phone numbers to destinations array
  trimmedContacts.forEach((contact) => {
    contact.destinations.forEach((destination) => {
      if (destination.type === "phone_number") {
        const formattedPhoneNumber = removePhoneNumberFormatting(destination.value);
        if (!isPhoneNumberExistsInDestinations(newDestinations, formattedPhoneNumber)) {
          newDestinations.push({
            id: formattedPhoneNumber,
            label: formattedPhoneNumber,
            value: formattedPhoneNumber,
            type: "phone_number",
          });
        }
      } else if (destination.type === "email" || destination.type === "custom") {
        if (!isInDestinations(newDestinations, destination.value)) {
          newDestinations.push({
            id: destination.value,
            label: destination.value,
            value: destination.value,
            type: destination.type,
          });
        }
      }
    });
  });

  const updatedCompanyDirectory = {
    contacts: contactsUiToContacts(trimmedContacts),
    destinations: newDestinations,
    netsapiensSyncDomain: companyDirectory.updatedNetsapiensSyncDomain.trim(),
    contactsMode: companyDirectory.updatedContactsMode,
  };

  return updatedCompanyDirectory;
};

const isPhoneNumberExistsInDestinations = (destinations, phoneNumber) => {
  return destinations.some(
    (destination) => destination.type === "phone_number" && removePhoneNumberFormatting(destination.id) === phoneNumber
  );
};

const isInDestinations = (destinations, value) => {
  return destinations.some((destination) => destination.id === value);
};

export const isEmptyContact = (contact) => {
  if (contact.name === "" && isEmptyDestinations(contact.destinations)) {
    return true;
  }

  return false;
};

export const isEmptyDestinations = (destinations) => {
  if (destinations.length === 0) {
    return true;
  }

  for (var i = 0; i < destinations.length; i++) {
    if (destinations[i].value !== "") {
      return false;
    }
  }

  return true;
};
